import { useState } from "react";
import { UserPlus, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

/**
 * Kunde laedt einen Kollegen ein. Der Kollege bekommt eine Mail, vergibt ein
 * Passwort und wird automatisch mit derselben Kundennummer verknuepft.
 */
export function InviteColleagueButton({ user }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const hasCustomerNo = !!(user?.customer_no || "").trim();

  const reset = () => { setEmail(""); setSentTo(null); setSending(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben");
      return;
    }
    setSending(true);
    try {
      await api.post("/customer/invite-colleague", {
        email: value,
        frontend_url: window.location.origin.startsWith("http") ? window.location.origin : "",
      });
      setSentTo(value);
    } catch (err) {
      toast.error(getErrorMsg(err, "Einladung konnte nicht gesendet werden"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => { reset(); setOpen(true); }}
        className="text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50 hover:text-fuchsia-800"
        data-testid="invite-colleague-btn"
      >
        <UserPlus className="w-4 h-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Kollegen einladen</span>
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="sm:max-w-md" data-testid="invite-colleague-dialog">
          <DialogHeader>
            <DialogTitle>Kollegen einladen</DialogTitle>
            <DialogDescription>
              Ihr Kollege bekommt eine E-Mail, vergibt ein Passwort und sieht danach
              dieselben Aufträge, Geräte und Daten wie Sie.
            </DialogDescription>
          </DialogHeader>

          {!hasCustomerNo ? (
            <div className="flex gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p>Ihrem Konto ist noch keine Kundennummer zugeordnet. Bitte wenden Sie sich an Eventenergie.</p>
            </div>
          ) : sentTo ? (
            <div className="text-center space-y-3 py-2">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-gray-700">
                Die Einladung wurde an <strong>{sentTo}</strong> gesendet.
                Der Link ist 7 Tage gültig.
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={reset}>Weiteren einladen</Button>
                <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={() => setOpen(false)}>Fertig</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">E-Mail-Adresse des Kollegen</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="invite-email"
                    type="email"
                    autoFocus
                    placeholder="kollege@firma.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    data-testid="invite-colleague-email"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button
                  type="submit"
                  disabled={sending || !email.trim()}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                  data-testid="invite-colleague-submit"
                >
                  {sending ? "Wird gesendet..." : "Einladung senden"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
